import React, { CSSProperties, useEffect, useState } from "react"
import { Shield } from "lucide-react"

const MESSAGES = [
  "Estabelecendo canal seguro...",
  "Verificando credenciais militares...",
  "Consultando registro de patente...",
  "Carregando dados operacionais...",
  "Inicializando painel de comando...",
  "Sincronizando módulos do sistema...",
]

const PARTICLES: { id:number; x:number; y:number; size:number; delay:number; dur:number }[] = [
  { id:0,  x:5,  y:15, size:2, delay:0.0, dur:7  },
  { id:1,  x:20, y:75, size:3, delay:1.2, dur:9  },
  { id:2,  x:37, y:30, size:2, delay:0.5, dur:6  },
  { id:3,  x:55, y:85, size:4, delay:2.1, dur:8  },
  { id:4,  x:70, y:10, size:2, delay:0.8, dur:10 },
  { id:5,  x:82, y:55, size:3, delay:1.5, dur:7  },
  { id:6,  x:92, y:78, size:2, delay:3.0, dur:9  },
  { id:7,  x:12, y:45, size:3, delay:2.4, dur:6  },
  { id:8,  x:28, y:92, size:2, delay:0.3, dur:11 },
  { id:9,  x:48, y:18, size:4, delay:1.8, dur:8  },
  { id:10, x:63, y:60, size:2, delay:0.7, dur:7  },
  { id:11, x:78, y:35, size:3, delay:2.8, dur:9  },
  { id:12, x:88, y:22, size:2, delay:1.1, dur:6  },
  { id:13, x:95, y:45, size:3, delay:0.4, dur:10 },
  { id:14, x:8,  y:68, size:2, delay:3.5, dur:8  },
  { id:15, x:42, y:50, size:4, delay:2.2, dur:7  },
  { id:16, x:58, y:95, size:2, delay:0.9, dur:9  },
  { id:17, x:73, y:72, size:3, delay:1.6, dur:6  },
  { id:18, x:85, y:5,  size:2, delay:2.7, dur:11 },
  { id:19, x:25, y:58, size:3, delay:0.2, dur:8  },
  { id:20, x:33, y:12, size:2, delay:3.2, dur:7  },
  { id:21, x:52, y:38, size:4, delay:1.4, dur:9  },
  { id:22, x:67, y:88, size:2, delay:2.9, dur:6  },
  { id:23, x:90, y:62, size:3, delay:0.6, dur:10 },
  { id:24, x:15, y:28, size:2, delay:1.9, dur:8  },
]

const MONO = "'Geist Mono','Courier New',monospace"
const GREEN = "rgba(74,180,100,0.9)"

const cornerStyle = (pos:"tl"|"tr"|"bl"|"br"): CSSProperties => ({
  position:"absolute", width:"18px", height:"18px",
  borderTop:    (pos==="tl"||pos==="tr") ? `2px solid ${GREEN}` : undefined,
  borderBottom: (pos==="bl"||pos==="br") ? `2px solid ${GREEN}` : undefined,
  borderLeft:   (pos==="tl"||pos==="bl") ? `2px solid ${GREEN}` : undefined,
  borderRight:  (pos==="tr"||pos==="br") ? `2px solid ${GREEN}` : undefined,
  top:    pos.startsWith("t") ? "-1px" : undefined,
  bottom: pos.startsWith("b") ? "-1px" : undefined,
  left:   pos.endsWith("l")   ? "-1px" : undefined,
  right:  pos.endsWith("r")   ? "-1px" : undefined,
})

interface Props { visible: boolean }

export function MilitaryLoginLoader({ visible }: Props) {
  const [msgIdx,   setMsgIdx]   = useState(0)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!visible) { setMsgIdx(0); setProgress(0); return }

    const msgInterval = setInterval(() => setMsgIdx(p => (p + 1) % MESSAGES.length), 600)

    let prog = 0
    const progInterval = setInterval(() => {
      prog += 1.6
      if (prog >= 93) { clearInterval(progInterval); prog = 93 }
      setProgress(prog)
    }, 50)

    return () => { clearInterval(msgInterval); clearInterval(progInterval) }
  }, [visible])

  if (!visible) return null

  return (
    <>
      <style>{`
        @keyframes mlGridDrift  { 0%{background-position:0 0} 100%{background-position:50px 50px} }
        @keyframes mlScan       { 0%{top:-2px} 100%{top:100vh} }
        @keyframes mlFloat      { 0%{transform:translate(0,0) scale(1);opacity:.4}
                                  50%{transform:translate(16px,-20px) scale(1.3);opacity:.75}
                                  100%{transform:translate(-10px,14px) scale(.8);opacity:.3} }
        @keyframes mlPulseGlow  { 0%,100%{box-shadow:0 0 24px rgba(74,180,100,.10)}
                                  50%    {box-shadow:0 0 44px rgba(74,180,100,.30)} }
        @keyframes mlFadeIn     { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }
        @keyframes mlOverlayIn  { from{opacity:0} to{opacity:1} }
      `}</style>

      {/* overlay */}
      <div style={{
        position:"fixed", inset:0, zIndex:9999,
        background:"linear-gradient(135deg,#050c07 0%,#091508 40%,#050c07 100%)",
        display:"flex", alignItems:"center", justifyContent:"center",
        overflow:"hidden", animation:"mlOverlayIn .3s ease-out",
      }}>
        {/* grade animada */}
        <div style={{
          position:"absolute", inset:0,
          backgroundImage:`linear-gradient(rgba(74,180,100,.06) 1px,transparent 1px),
                           linear-gradient(90deg,rgba(74,180,100,.06) 1px,transparent 1px)`,
          backgroundSize:"50px 50px",
          animation:"mlGridDrift 22s linear infinite",
        }} />

        {/* linha de scan */}
        <div style={{
          position:"absolute", left:0, right:0, height:"1px", zIndex:1,
          background:"linear-gradient(90deg,transparent,rgba(74,180,100,.55),transparent)",
          animation:"mlScan 4s linear infinite",
        }} />

        {/* partículas */}
        {PARTICLES.map(p => (
          <div key={p.id} style={{
            position:"absolute",
            left:`${p.x}%`, top:`${p.y}%`,
            width:`${p.size}px`, height:`${p.size}px`,
            borderRadius:"50%",
            background:"rgba(74,200,110,.55)",
            boxShadow:"0 0 6px rgba(74,200,110,.7)",
            animation:`mlFloat ${p.dur}s ease-in-out ${p.delay}s infinite alternate`,
          }} />
        ))}

        {/* painel */}
        <div style={{
          position:"relative", zIndex:2,
          display:"flex", flexDirection:"column",
          alignItems:"center", gap:"1.5rem",
          padding:"3rem 3.5rem", minWidth:"360px",
          background:"rgba(8,20,11,.9)",
          border:"1px solid rgba(74,180,100,.22)",
          backdropFilter:"blur(16px)",
          boxShadow:"0 30px 80px rgba(0,0,0,.7),0 0 60px rgba(30,100,50,.06)",
        }}>
          <span style={cornerStyle("tl")} />
          <span style={cornerStyle("tr")} />
          <span style={cornerStyle("bl")} />
          <span style={cornerStyle("br")} />

          {/* ícone */}
          <div style={{
            width:"76px", height:"76px", borderRadius:"50%",
            background:"rgba(74,180,100,.06)",
            border:"2px solid rgba(74,180,100,.35)",
            display:"flex", alignItems:"center", justifyContent:"center",
            color:"rgba(100,200,120,.9)",
            animation:"mlPulseGlow 2s ease-in-out infinite",
          }}>
            <Shield size={36} />
          </div>

          {/* status */}
          <p style={{
            fontFamily:MONO, fontSize:"10px",
            letterSpacing:"4px", color:"rgba(74,180,100,.5)",
            textTransform:"uppercase", margin:0,
          }}>// AUTENTICANDO //</p>

          {/* mensagem rotativa */}
          <p key={msgIdx} style={{
            fontFamily:MONO, fontSize:"13px",
            color:"rgba(190,230,205,.9)", letterSpacing:".4px",
            textAlign:"center", minHeight:"20px", margin:0,
            animation:"mlFadeIn .3s ease-out",
          }}>
            &gt;&nbsp;{MESSAGES[msgIdx]}
          </p>

          {/* barra de progresso */}
          <div style={{
            width:"100%", height:"3px",
            background:"rgba(74,180,100,.1)",
            position:"relative", overflow:"hidden",
          }}>
            <div style={{
              position:"absolute", inset:"0 auto 0 0",
              width:`${progress}%`,
              background:"linear-gradient(90deg,rgba(74,180,100,.55),rgba(120,220,140,.9))",
              boxShadow:"0 0 8px rgba(74,180,100,.8)",
              transition:"width .12s linear",
            }} />
          </div>

          {/* percentual */}
          <p style={{
            fontFamily:MONO, fontSize:"10px",
            letterSpacing:"2px", color:"rgba(74,180,100,.45)",
            margin:0,
          }}>
            {Math.floor(progress)}% COMPLETO
          </p>
        </div>
      </div>
    </>
  )
}
